/**
 * Client/Insurance-related utility functions
 * Extracted from App.js for better organization and reusability
 */

/**
 * Get client's primary insurance from either the new insurances array or legacy fields
 * @param {Object} client - Client object
 * @param {Array} insurancesList - Optional list of insurance objects for ID lookup
 * @returns {Object|null} - { company, member_id, group_number, eligibility_status } or null
 */
export const getClientPrimaryInsurance = (client, insurancesList = []) => {
  if (!client) return null;
  
  // First check insurances array (new structure)
  if (client.insurances && Array.isArray(client.insurances) && client.insurances.length > 0) {
    const primary = client.insurances.find(i => i.priority === 1 && i.status === 'active');
    if (primary) {
      return {
        company: primary.company,
        member_id: primary.member_id,
        group_number: primary.group_number,
        eligibility_status: primary.eligibility_status
      };
    }
  }
  
  // Fallback to legacy fields
  if (client.primary_insurance) {
    return {
      company: client.primary_insurance,
      member_id: client.primary_member_id,
      group_number: client.primary_group_number
    };
  }
  
  // Check if there's an insurance_id reference
  if (client.primary_insurance_id && insurancesList.length > 0) {
    const ins = insurancesList.find(i => i.id === client.primary_insurance_id);
    if (ins) {
      return {
        company: ins.name,
        member_id: client.primary_member_id,
        group_number: client.primary_group_number
      };
    }
  }
  
  return null;
};

/**
 * Get ALL active insurances for a client (for billing/claims)
 * @param {Object} client - Client object
 * @param {Array} insurancesList - Optional list of insurance objects for ID lookup
 * @returns {Array} - Array of { priority, company, member_id, group_number, ... } sorted by priority
 */
export const getClientAllInsurances = (client, insurancesList = []) => {
  if (!client) return [];
  
  const result = [];
  
  // First check insurances array (new structure)
  if (client.insurances && Array.isArray(client.insurances) && client.insurances.length > 0) {
    const activeInsurances = client.insurances
      .filter(i => i.status === 'active')
      .sort((a, b) => (a.priority || 99) - (b.priority || 99));
    
    for (const ins of activeInsurances) {
      result.push({
        priority: ins.priority,
        company: ins.company,
        member_id: ins.member_id,
        group_number: ins.group_number,
        eligibility_status: ins.eligibility_status,
        copay_amount: ins.copay_amount,
        deductible_amount: ins.deductible_amount,
        relationship_to_insured: ins.relationship_to_insured,
        insured_name: ins.insured_name
      });
    }
    
    if (result.length > 0) return result;
  }
  
  // Fallback to legacy fields
  if (client.primary_insurance) {
    result.push({
      priority: 1,
      company: client.primary_insurance,
      member_id: client.primary_member_id,
      group_number: client.primary_group_number
    });
  }
  if (client.secondary_insurance) {
    result.push({
      priority: 2,
      company: client.secondary_insurance,
      member_id: client.secondary_member_id,
      group_number: client.secondary_group_number
    });
  }
  if (client.tertiary_insurance) {
    result.push({
      priority: 3,
      company: client.tertiary_insurance,
      member_id: client.tertiary_member_id,
      group_number: client.tertiary_group_number
    });
  }
  
  return result;
};

/**
 * Age classification from date_of_birth (display-only).
 * Returns { age, label, cls } or null when DOB is missing/invalid.
 *   Under 18 → amber | Under 21 → sky | Adult (21+) → slate
 */
export const getAgeClassification = (dobIso) => {
  if (!dobIso) return null;
  let dob = null;
  const m = String(dobIso).match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (m) dob = new Date(+m[1], +m[2] - 1, +m[3]);
  else {
    const d = new Date(dobIso);
    dob = Number.isNaN(d.getTime()) ? null : d;
  }
  if (!dob) return null;
  const today = new Date();
  let age = today.getFullYear() - dob.getFullYear();
  const monthDiff = today.getMonth() - dob.getMonth();
  if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < dob.getDate())) age -= 1;
  if (age < 0 || age > 130) return null;
  if (age < 18) return { age, label: 'Under 18', cls: 'border-amber-300 bg-amber-50 text-amber-700' };
  if (age < 21) return { age, label: 'Under 21', cls: 'border-sky-300 bg-sky-50 text-sky-700' };
  return { age, label: 'Adult', cls: 'border-slate-300 bg-slate-50 text-slate-600' };
};
